import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UsePaginationStateOptions {
  defaultPage?: number;
  defaultSize?: number;
  syncUrl?: boolean;
  pageParam?: string;
  sizeParam?: string;
}

export interface UsePaginationStateReturn {
  page: number;
  size: number;
  offset: number;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  reset: () => void;
  resetOnChange: (deps: unknown[]) => void;
}

export function usePaginationState(options: UsePaginationStateOptions = {}): UsePaginationStateReturn {
  const {
    defaultPage = 1,
    defaultSize = 25,
    syncUrl = false,
    pageParam = 'page',
    sizeParam = 'size',
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  const [localPage, setLocalPage] = useState<number>(() => {
    if (syncUrl) {
      const raw = searchParams.get(pageParam);
      const parsed = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(parsed) && parsed >= 1) return parsed;
    }
    return defaultPage;
  });

  const [localSize, setLocalSize] = useState<number>(() => {
    if (syncUrl) {
      const raw = searchParams.get(sizeParam);
      const parsed = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(parsed) && parsed >= 1) return parsed;
    }
    return defaultSize;
  });

  const setPage = useCallback((next: number) => {
    const normalized = Math.max(1, Math.floor(next));
    setLocalPage(normalized);
    if (syncUrl) {
      setSearchParams(prev => {
        const nextParams = new URLSearchParams(prev);
        if (normalized === defaultPage) {
          nextParams.delete(pageParam);
        } else {
          nextParams.set(pageParam, String(normalized));
        }
        return nextParams;
      }, { replace: true });
    }
  }, [syncUrl, setSearchParams, pageParam, defaultPage]);

  const setSize = useCallback((next: number) => {
    const normalized = Math.max(1, Math.floor(next));
    setLocalSize(normalized);
    setLocalPage(defaultPage);
    if (syncUrl) {
      setSearchParams(prev => {
        const nextParams = new URLSearchParams(prev);
        if (normalized === defaultSize) {
          nextParams.delete(sizeParam);
        } else {
          nextParams.set(sizeParam, String(normalized));
        }
        nextParams.delete(pageParam);
        return nextParams;
      }, { replace: true });
    }
  }, [syncUrl, setSearchParams, sizeParam, pageParam, defaultSize, defaultPage]);

  const reset = useCallback(() => {
    setPage(defaultPage);
  }, [setPage, defaultPage]);

  // Stable callback reference to avoid re-triggering reset on every render.
  const firstRunRef = useRef(true);
  const lastDepsRef = useRef<unknown[]>([]);

  const resetOnChange = useCallback((deps: unknown[]) => {
    // Intentional: this function is called inline in the component body; the
    // caller passes a fresh deps array each render. We compare shallowly and
    // only reset when deps change after the initial mount.
    const prev = lastDepsRef.current;
    const changed =
      prev.length !== deps.length ||
      deps.some((dep, i) => !Object.is(dep, prev[i]));

    if (firstRunRef.current) {
      firstRunRef.current = false;
      lastDepsRef.current = deps;
      return;
    }

    if (changed) {
      lastDepsRef.current = deps;
      setLocalPage(defaultPage);
      if (syncUrl) {
        setSearchParams(prevParams => {
          const nextParams = new URLSearchParams(prevParams);
          nextParams.delete(pageParam);
          return nextParams;
        }, { replace: true });
      }
    }
  }, [defaultPage, syncUrl, setSearchParams, pageParam]);

  // Sync local page back if URL changes externally (back/forward nav).
  useEffect(() => {
    if (!syncUrl) return;
    const raw = searchParams.get(pageParam);
    const parsed = raw ? parseInt(raw, 10) : defaultPage;
    const normalized = Number.isFinite(parsed) && parsed >= 1 ? parsed : defaultPage;
    if (normalized !== localPage) {
      // Syncing local state from URL when user navigates via back/forward.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocalPage(normalized);
    }
  }, [searchParams, syncUrl, pageParam, defaultPage, localPage]);

  const offset = useMemo(() => (localPage - 1) * localSize, [localPage, localSize]);

  return {
    page: localPage,
    size: localSize,
    offset,
    setPage,
    setSize,
    reset,
    resetOnChange,
  };
}
